import {
  AddressSearchSelect,
  CampaignSearchSelect,
  CooperativeSearchSelect,
  CounterpartySearchSelect,
  ProductSearchSelect,
} from '@/components/form-fields';
import { AddressType, ProductNatureType, WaybillStatusType } from '@/types/enums';
import { buildDropdownOptionsFromEnum } from '@/utils/common';
import { getTranslatedString } from '@/utils';

const waybillStatusOptions = () =>
  (Object.values(WaybillStatusType) as string[]).map((value) => ({
    value,
    label: getTranslatedString(`waybills.status.${value.replace(/ /g, '_')}`),
  }));

/** Editable waybill entity fields + read-only "latest weighing" summary. */
export const getFormFields = () => {
  return [
    // --- Editable waybill fields ---
    {
      name: 'waybillNumber',
      componentName: 'input',
      params: {
        i18nLabel: 'waybills.waybillNumber',
      },
      rules: [{ required: true, max: 50 }],
    },
    {
      name: 'productId',
      component: ProductSearchSelect,
      params: {
        i18nLabel: 'waybills.product',
      },
    },
    {
      name: 'campaignId',
      component: CampaignSearchSelect,
      params: {
        i18nLabel: 'waybills.campaign',
      },
    },
    {
      name: 'productNature',
      componentName: 'dropdown',
      params: {
        i18nLabel: 'waybills.productNature',
        options: buildDropdownOptionsFromEnum(ProductNatureType),
      },
    },
    {
      // Net weight as declared on the PDF waybill (distinct from
      // accepted_weight which is the at-reception weighing). Auto-filled
      // during PDF ingestion; editable so the operator can correct it.
      name: 'declaredNetWeightKg',
      componentName: 'input-number',
      params: {
        i18nLabel: 'waybills.declaredNetWeightKg',
      },
      permissionName: 'waybill.declaredNetWeightKg',
    },
    {
      name: 'status',
      componentName: 'dropdown',
      params: {
        i18nLabel: 'waybills.status',
        options: waybillStatusOptions(),
      },
      permissionName: 'waybill.status',
    },
    {
      name: 'factoryId',
      component: AddressSearchSelect,
      params: {
        i18nLabel: 'waybills.factory',
        type: AddressType.Factory,
      },
      rules: [{ required: false }],
    },
    {
      name: 'harborId',
      component: AddressSearchSelect,
      params: {
        i18nLabel: 'waybills.harbor',
        type: AddressType.Harbor,
      },
      rules: [{ required: false }],
      permissionName: 'waybill.harborId',
    },
    {
      name: 'cooperativeId',
      component: CooperativeSearchSelect,
      params: {
        i18nLabel: 'waybills.cooperative',
      },
      permissionName: 'waybill.cooperativeId',
    },
    {
      name: 'exporterId',
      component: CounterpartySearchSelect,
      params: {
        i18nLabel: 'waybills.exporter',
      },
      permissionName: 'waybill.exporterId',
    },
    // --- Read-only: latest weighing summary (from weighing_analyses) ---
    {
      name: 'latestWeighingCode',
      componentName: 'input',
      params: {
        i18nLabel: 'waybills.latestWeightingCode',
        readOnly: true,
      },
    },
    {
      name: 'latestNatGrade',
      componentName: 'input',
      params: {
        i18nLabel: 'waybills.latestNatGrade',
        readOnly: true,
      },
    },
    {
      name: 'latestFccGrade',
      componentName: 'input',
      params: {
        i18nLabel: 'waybills.latestFccGrade',
        readOnly: true,
      },
    },
    {
      name: 'latestBags',
      componentName: 'input-number',
      params: { i18nLabel: 'waybills.latestBags', readOnly: true },
    },
    {
      name: 'latestAcceptedWeight',
      componentName: 'input-number',
      params: { i18nLabel: 'waybills.latestAcceptedWeight', readOnly: true },
    },
  ];
};
